/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import { ApiError } from 'data/openapi/core/ApiError';
import type { ApiRequestOptions } from 'data/openapi/core/ApiRequestOptions';
import type { ApiResult } from 'data/openapi/core/ApiResult';
import { CancelablePromise } from 'data/openapi/core/CancelablePromise';
import type { OnCancel } from 'data/openapi/core/CancelablePromise';
import type { OpenAPIConfig } from 'data/openapi/core/OpenAPI';
import { apiStore } from 'data/stores/api';
import { errorTransformer } from 'data/utils/helpers';
import { ErrorType } from 'data/constants';

// TYPES
type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

// CONSTANTS
const specificClientErrorStatuses = [400, 401, 404];
const errorTypeMapper = {
  400: ErrorType.VALIDATION,
  401: ErrorType.UNAUTHORIZED,
  404: ErrorType.NOT_FOUND,
  403: ErrorType.FORBIDDEN,
  503: ErrorType.UNAVAILABLE,
};

// HELPER FUNCTIONS
const isDefined = <T>(value: T | null | undefined): value is Exclude<T, null | undefined> => {
  return value !== undefined && value !== null;
};

const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

const isStringWithValue = (value: unknown): value is string => {
  return isString(value) && value !== '';
};

const shouldSkipNotFoundPage = (method: string, url: string) => {
  const isStatusRequest = url.includes('/status');
  const isPruneRequest = url.includes('/prune');
  const isBackupRequest = url.includes('/backup');
  const isRestoreRequest = url.includes('/restore');
  const isPartiesRequest = url.includes('/jsonapi/v1/parties');
  const isUsersRequest = url.includes('/jsonapi/v1/users');
  const isConsoleRequest = url.includes('/console');
  const isLogsRequest = url.includes('/logs');
  const isIdDumpsScheduleRequest = url.includes('/id-dumps');
  const isHttpProxyAccessTokenRequest = url.includes('/validator-api-access-token');

  return (
    method === 'GET' && (isStatusRequest || isPartiesRequest || isPruneRequest || isBackupRequest || isRestoreRequest || isConsoleRequest || isLogsRequest || isUsersRequest || isIdDumpsScheduleRequest)
  ) || (method === 'POST' && isHttpProxyAccessTokenRequest);
};

const shouldSkipUnavailablePage = (method: string, url: string) => {
  const isStatusRequest = url.includes('/status');

  return method === 'GET' && isStatusRequest;
}

const isBlob = (value: any): value is Blob => {
  return (
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    typeof value.stream === 'function' &&
    typeof value.arrayBuffer === 'function' &&
    typeof value.constructor === 'function' &&
    typeof value.constructor.name === 'string' &&
    /^(Blob|File)$/.test(value.constructor.name) &&
    /^(Blob|File)$/.test(value[Symbol.toStringTag])
  );
};

const isFormData = (value: unknown): value is FormData => {
  return value instanceof FormData;
};

const base64 = (str: string): string => {
  try {
    return btoa(str);
  } catch (err) {
    return Buffer.from(str).toString('base64');
  }
};

const getQueryString = (params: Record<string, unknown>): string => {
  const qs: string[] = [];

  const append = (key: string, value: unknown) => {
    qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  };

  const process = (key: string, value: any) => {
    if (isDefined(value)) {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          process(key, v);
        });
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([k, v]) => {
          process(`${key}[${k}]`, v);
        });
      } else {
        append(key, value);
      }
    }
  };

  Object.entries(params).forEach(([key, value]) => {
    process(key, value);
  });

  if (qs.length > 0) {
    return `?${qs.join('&')}`;
  }

  return '';
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
  const encoder = config.ENCODE_PATH ?? encodeURI;

  const path = options.url
    .replace('{api-version}', config.VERSION)
    .replace(/{(.*?)}/g, (substring: string, group: string) => {
      if (options.path?.hasOwnProperty(group)) {
        return encoder(String(options.path[group]));
      }
      return substring;
    });

  const url = `${config.BASE}${path}`;
  if (options.query) {
    return `${url}${getQueryString(options.query)}`;
  }
  return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
  if (options.formData) {
    const formData = new FormData();

    const process = (key: string, value: unknown) => {
      if (isString(value) || isBlob(value)) {
        formData.append(key, value);
      } else {
        formData.append(key, JSON.stringify(value));
      }
    };

    Object.entries(options.formData)
      .filter(([_, value]) => isDefined(value))
      .forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => process(key, v));
        } else {
          process(key, value);
        }
      });

    return formData;
  }
  return undefined;
};

const resolve = async <T>(options: ApiRequestOptions, resolver?: T | Resolver<T>): Promise<T | undefined> => {
  if (typeof resolver === 'function') {
    return (resolver as Resolver<T>)(options);
  }
  return resolver;
};

const getHeaders = async (config: OpenAPIConfig, options: ApiRequestOptions): Promise<Headers> => {
  const token = await resolve(options, config.TOKEN);
  const username = await resolve(options, config.USERNAME);
  const password = await resolve(options, config.PASSWORD);
  const additionalHeaders = await resolve(options, config.HEADERS);

  const headers = Object.entries({
    Accept: 'application/json',
    ...additionalHeaders,
    ...options.headers,
  })
    .filter(([_, value]) => isDefined(value))
    .reduce(
      (headers, [key, value]) => ({
        ...headers,
        [key]: String(value),
      }),
      {} as Record<string, string>,
    );

  if (isStringWithValue(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (isStringWithValue(username) && isStringWithValue(password)) {
    const credentials = base64(`${username}:${password}`);
    headers['Authorization'] = `Basic ${credentials}`;
  }

  if (options.body) {
    if (options.mediaType) {
      headers['Content-Type'] = options.mediaType;
    } else if (isBlob(options.body)) {
      headers['Content-Type'] = options.body.type || 'application/octet-stream';
    } else if (isString(options.body)) {
      headers['Content-Type'] = 'text/plain';
    } else if (!isFormData(options.body)) {
      headers['Content-Type'] = 'application/json';
    }
  }

  return new Headers(headers);
};

const getRequestBody = (options: ApiRequestOptions): unknown => {
  if (options.body) {
    if (options.mediaType?.includes('/json')) {
      return JSON.stringify(options.body);
    } else if (isString(options.body) || isBlob(options.body) || isFormData(options.body)) {
      return options.body;
    } else {
      return JSON.stringify(options.body);
    }
  }
  return undefined;
};

export const sendRequest = async (
  config: OpenAPIConfig,
  options: ApiRequestOptions,
  url: string,
  body: any,
  formData: FormData | undefined,
  headers: Headers,
  onCancel: OnCancel,
): Promise<Response> => {
  const controller = new AbortController();

  const request: RequestInit = {
    headers,
    body: body ?? formData,
    method: options.method,
    signal: controller.signal,
  };

  if (config.WITH_CREDENTIALS) {
    request.credentials = config.CREDENTIALS;
  }

  onCancel(() => controller.abort());

  return await fetch(url, request);
};

const getResponseHeader = (response: Response, responseHeader?: string): string | undefined => {
  if (responseHeader) {
    const content = response.headers.get(responseHeader);
    if (isString(content)) {
      return content;
    }
  }
  return undefined;
};

const getResponseBody = async (response: Response): Promise<any> => {
  if (response.status !== 204) {
    try {
      const contentType = response.headers.get('Content-Type');
      if (contentType) {
        const isJSON = contentType.toLowerCase().startsWith('application/json');
        const isBLOB = contentType.toLowerCase().includes('zip') || contentType.toLowerCase().includes('octet-stream');
        if (isJSON) {
          return await response.json();
        } else if (isBLOB) {
          return await response.blob();
        } else {
          return await response.text();
        }
      }
    } catch (error) {
      console.error(error);
    }
  }
  return undefined;
};

const catchErrorCodes = (options: ApiRequestOptions, result: ApiResult): void => {
  const errors: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    ...options.errors,
  };

  const error = errors[result.status];

  if (error) {
    throw new ApiError(options, result, result?.body?.message);
  }

  if (!result.ok) {
    throw new ApiError(options, result, 'Generic Error');
  }
};

const setErrorInStore = (status: number, errorPayload: string) => {
  if (errorTypeMapper[status]) {
    apiStore.setState({ error: { type: errorTypeMapper[status], payload: errorPayload } });
  }
};

const formatFieldErrors = (fieldErrors: Record<string, string>): string => {
  if (!fieldErrors || Object.keys(fieldErrors).length === 0) {
    return '';
  }

  const formattedErrors = Object.entries(fieldErrors)
    .map(([field, error]) => `${field}: ${error}`)
    .join('; ');

  return ` Field errors: ${formattedErrors}`;
};

const handleValidationError = ({ request, status, body }: any, errorPayload: string) => {
  const isPostRequest = request.method?.toUpperCase() === 'POST';
  const isPutRequest = request.method?.toUpperCase() === 'PUT';
  const isDeleteRequest = request.method?.toUpperCase() === 'DELETE';

  if (isPostRequest || isPutRequest) {
    // api-json-gateway return generic response, let's show response in the toast message only
    let errorMessage = body.errors || body.message || body.cause ? errorPayload : 'Validation error. \n\t';

    // Append fieldErrors if they exist
    if (body.fieldErrors && typeof body.fieldErrors === 'object') {
      const fieldErrorsText = formatFieldErrors(body.fieldErrors);
      errorMessage += fieldErrorsText;
    }

    setErrorInStore(status, errorMessage);
  }

  if (isDeleteRequest) setErrorInStore(status, errorPayload);
};

const handleNotFoundError = ({ request, status }: any, url: string, errorPayload: string) => {
  if (request.method === 'POST' && url.includes('/jsonapi/v2/users')) {
    setErrorInStore(400, errorPayload);
    return;
  }

  if (shouldSkipNotFoundPage(request.method, url))
    return apiStore.setState({
      error: { type: ErrorType.IGNORED },
    });

  setErrorInStore(status, errorPayload);
};

const handleUnavailableError = ({ request, status }: any, url: string, errorPayload: string) => {
  if (shouldSkipUnavailablePage(request.method, url)) {
    return apiStore.setState({
      error: { type: ErrorType.IGNORED },
    });
  }

  setErrorInStore(status, errorPayload);
};

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T>(config: OpenAPIConfig, options: ApiRequestOptions): CancelablePromise<T> => {
  return new CancelablePromise(async (resolve, reject, onCancel) => {
    const url = getUrl(config, options);
    try {
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options);

      if (!onCancel.isCancelled) {
        const response = await sendRequest(config, options, url, body, formData, headers, onCancel);
        const responseBody = await getResponseBody(response);
        const responseHeader = getResponseHeader(response, options.responseHeader);

        const result: ApiResult = {
          url,
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          body: responseHeader ?? responseBody,
        };

        catchErrorCodes(options, result);

        resolve(result.body);
      }
    } catch (error) {
      const errorPayload = errorTransformer(error);
      const { status } = error;
      const isSpecificClientError = specificClientErrorStatuses.find((el) => el === status);

      switch (status) {
        case 400:
          handleValidationError(error, errorPayload);
          break;
        case 404:
          handleNotFoundError(error, url, errorPayload);
          break;
        case 503:
          handleUnavailableError(error, url, errorPayload);
          break;
        default:
          setErrorInStore(status, errorPayload);
          break;
      }

      if (status >= 500 && status !== 503) {
        apiStore.setState({ error: { type: ErrorType.SERVER_ERROR, payload: errorPayload } });
      }

      if (status > 400 && status < 500 && !isSpecificClientError) {
        apiStore.setState({ error: { type: ErrorType.CLIENT_RESPONSIBLE, payload: errorPayload } });
      }

      if (!apiStore.getState()?.error) {
        apiStore.setState({ error: { type: ErrorType.UNKNOWN, payload: errorPayload } });
      }

      reject(error);
    }
  });
};
